import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  Inject,
  ChangeDetectorRef,
  Optional,
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  CallHubService,
  IncomingCallEvent,
  VideoStatusEvent,
} from '../../../../core/services/call-hub/call-hub.service';
import { Subscription } from 'rxjs';
import { ToastService } from '../../../../core/services/toast/toast.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AudioUnlockService } from '../../../../core/services/audio/audio-unlock.service';
import { CallOverlayService } from '../../../../core/services/call-overlay/call-overlay.service';
import { CurrentUserService } from '../../../../core/services/auth/current-user.service';
import { RemoteHealthService } from '../../../../core/services/remote-health/remote-health.service';
import { SendRemoteHealthModel } from '../../../../core/models/remote-health/remote-health.model';
import { ModalService } from '../../../../core/services/modal/modal.service';
import { MediaPermissionStore } from '../../../../core/services/consent/media-permission.store';
import { DoctorNamePipe } from '../../../../core/pipes/doctor-name.pipe';
import { CallMediaConsentOrchestratorService } from '../../../../core/services/consent/call-media-consent-orchestrator.service';

@Component({
  selector: 'app-video-call-modal',
  standalone: true,
  imports: [CommonModule, TranslateModule, DoctorNamePipe],
  templateUrl: './video-call-modal.component.html',
  styleUrls: ['./video-call-modal.component.scss'],
})
export class VideoCallModalComponent implements OnInit, OnDestroy {
  @Input() incomingCall?: IncomingCallEvent;
  @Input() calleeUserId?: string;
  @Input() patientId?: string;
  @Input() callType: any = 'Video';
  @Input() ringtoneAudio?: HTMLAudioElement;
  @Input() callContextType: any = 'Patient';
  @Input() counterpartyUserName?: string;
  @Input() counterpartyProfessionalTitle?: number | string | null;
  @Input() counterpartyBranchName?: string;
  @Input() counterpartyUserId?: string;

  @ViewChild('localVideo', { static: false })
  localVideo?: ElementRef<HTMLVideoElement>;
  @ViewChild('remoteVideo', { static: false })
  remoteVideo?: ElementRef<HTMLVideoElement>;
  isRemoteVideoOff = false;
  isRemoteAudioOff = false;
  private remoteVideoStatusFromEvent?: boolean;
  private remoteAudioStatusFromEvent?: boolean;
  private currentRemoteStream: MediaStream | null = null;
  @ViewChild('minRemoteVideo', { static: false })
  minRemoteVideo?: ElementRef<HTMLVideoElement>;

  inPrompt = false;
  inCall = false;
  isMuted = false;
  isVideoOff = false;
  isMinimized = false;
  isMinHovered = false;
  mediaPermissionDenied = false;
  canSendAudio = true;
  canSendVideo = true;

  minimizedLeft = 12;
  minimizedBottom = 12;
  minimizedWidth = 220;
  minimizedHeight = 140;

  private dragActive = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;

  private resizeActive = false;
  private resizeEdge = '';
  private resizeStartX = 0;
  private resizeStartY = 0;
  private resizeStartWidth = 0;
  private resizeStartHeight = 0;
  private resizeStartLeft = 0;
  private resizeStartBottom = 0;

  private dragMoved = false;
  private readonly DRAG_THRESHOLD = 6;

  callStartTime: number | null = null;
  callStartTimeDate: Date | null = null;
  duration = '00:00';
  private durationTimer?: any;
  private remoteHealthSent = false;

  private currentCallId?: string;
  private endTonePlayed = false;

  private subs: Subscription[] = [];

  private onKeyDown = (ev: KeyboardEvent) => {
    try {
      if (ev.key !== 'Escape') return;
      if (ev.repeat) return;
      if (this.inPrompt || !this.inCall) return;
      if (this.isMinimized) return;
      const active = document.activeElement as HTMLElement | null;
      if (active) {
        const tag = (active.tagName || '').toUpperCase();
        if (tag === 'INPUT' || tag === 'TEXTAREA' || active.isContentEditable)
          return;
      }
      if (ev.altKey || ev.ctrlKey || ev.metaKey || ev.shiftKey) return;

      try {
        this.toggleMinimize();
      } catch {}
      ev.preventDefault();
    } catch {}
  };

  private lastRemoteStream: MediaStream | null = null;
  private lastLocalStream: MediaStream | null = null;

  minPlaybackBlocked = false;
  private permissionDeniedToastShown = false;
  private localPermissionDenied = false;
  private initialLocalMediaSet = false;
  private browserPermissionToastShown = false;

  private _remoteMetadataHandler?: (this: HTMLVideoElement, ev: Event) => void;
  private _minRemoteMetadataHandler?: (
    this: HTMLVideoElement,
    ev: Event
  ) => void;

  constructor(
    @Inject(CallHubService) private vc: CallHubService,
    private toastService: ToastService,
    private translate: TranslateService,
    private audioUnlock: AudioUnlockService,
    private cdr: ChangeDetectorRef,
    private overlay: CallOverlayService,
    private currentUserService: CurrentUserService,
    private remoteHealthService: RemoteHealthService,
    private modalService: ModalService,
    private mediaPermissionStore: MediaPermissionStore,
    private callMediaConsentOrchestrator: CallMediaConsentOrchestratorService,
    @Inject(NgbActiveModal) @Optional() public activeModal?: NgbActiveModal
  ) {}

  get isVideoCall(): boolean {
    return this.isVideoCallType(this.incomingCall?.callType ?? this.callType);
  }

  get displayPeerName(): string {
    return (
      this.displayPeerNameRaw ||
      this.translate.instant('video-call.unknown-user')
    );
  }

  get displayPeerNameRaw(): string {
    const ic: any = this.incomingCall || {};
    return (
      this.normalizeOptionalString(ic.fromUserName) ||
      this.normalizeOptionalString(this.counterpartyUserName) ||
      this.normalizeOptionalString(this.calleeUserId)
    );
  }

  get displayPeerProfessionalTitle(): number | string | null {
    const ic: any = this.incomingCall || {};
    return (
      ic.fromUserProfessionalTitle ??
      ic.FromUserProfessionalTitle ??
      this.counterpartyProfessionalTitle ??
      null
    );
  }

  get displayPeerBranchName(): string | null {
    const ic: any = this.incomingCall || {};
    return (
      this.normalizeOptionalString(
        ic.fromUserBranchName ?? ic.FromUserBranchName
      ) ||
      this.normalizeOptionalString(this.counterpartyBranchName) ||
      null
    );
  }

  get showInCallPeerInfo(): boolean {
    return !this.inPrompt && !!this.displayPeerNameRaw;
  }

  get callerInitials(): string {
    const name = (this.displayPeerName || '').trim();
    if (!name) return 'U';
    const parts = name.split(/\s+/).filter(Boolean);
    const initials =
      (parts[0]?.[0] || '') + (parts.length > 1 ? parts[1]?.[0] || '' : '');
    return initials.toUpperCase() || 'U';
  }

  get callTypeLabel(): string {
    return this.translate.instant(
      this.isVideoCall
        ? 'video-call.wants-video-call'
        : 'video-call.wants-audio-call'
    );
  }

  get muteIcon(): string {
    return this.isMuted
      ? '/assets/icons/mic-off.svg'
      : '/assets/icons/mic-on.svg';
  }

  get cameraIcon(): string {
    return this.isVideoOff
      ? '/assets/icons/camera-off.svg'
      : '/assets/icons/camera-on.svg';
  }

  get hangupIcon(): string {
    return '/assets/icons/phone-off.svg';
  }

  get permissionInfoKey(): string | null {
    const sendAudio = this.canSendAudio && !this.isMuted;
    const sendVideo = this.isVideoCall && this.canSendVideo && !this.isVideoOff;
    const missingAudio = !sendAudio;
    const missingVideo = this.isVideoCall && !sendVideo;
    if (!missingAudio && !missingVideo) return null;
    if (missingAudio && missingVideo) return 'call.permission.partialAudioVideo';
    if (missingVideo) return 'call.permission.partialVideo';
    return 'call.permission.partialAudio';
  }

  private isVideoCallType(ct: any): boolean {
    if (typeof ct === 'number') return ct === 2;
    const s = String(ct || '').toLowerCase();
    return s === 'video';
  }

  async ngOnInit(): Promise<void> {
    this.vc.ensureSignaling().catch(() => {});

    try {
      this.overlay.maximize();
      this.isMinimized = false;
    } catch {}

    if (this.incomingCall) {
      this.inPrompt = true;
      this.currentCallId = this.incomingCall.callId;
    }

    try {
      const sm = this.overlay.getMinimized$().subscribe((v) => {
        if (this.inPrompt) return;
        this.isMinimized = !!v;
        try {
          this.cdr.detectChanges();
        } catch {}
      });
      this.subs.push(sm);
    } catch {}

    if (!this.incomingCall && (this.calleeUserId || this.patientId)) {
      this.startOutgoing();
    }

    const s = this.vc.onCallEnded().subscribe((ev) => {
      if (!ev) return;
      if (
        !this.currentCallId ||
        !ev.callId ||
        ev.callId === this.currentCallId
      ) {
        const reason = (ev.reason || '').toString();
        const isPermissionDenied = reason === 'PermissionDenied';
        if (isPermissionDenied && !this.localPermissionDenied) {
          this.toastService.show({
            header: this.translate.instant(
              'video-call.recipient-permission-denied-title'
            ),
            body: this.translate.instant(
              'video-call.recipient-permission-denied-text'
            ),
            classname: 'bg-warning text-white',
            delay: 6000,
          });
        }

        this.stopRingtone();
        this.playEndToneOnce();
        this.sendRemoteHealthData();
        this.inCall = false;
        this.stopDurationTimer();
        this.blurActiveElement();
        try {
          if (this.activeModal) this.activeModal.close('ended');
          else this.overlay.end();
        } catch {}
      }
    });
    this.subs.push(s);

    const rj = this.vc.onCallRejected().subscribe((ev) => {
      if (!ev) return;
      if (
        !this.currentCallId ||
        !ev.callId ||
        ev.callId === this.currentCallId
      ) {
        this.stopRingtone();
        const reason = (ev.reason || '').toString();
        const isPermissionDenied = reason === 'PermissionDenied';
        if (isPermissionDenied && !this.localPermissionDenied) {
          this.toastService.show({
            header: this.translate.instant(
              'video-call.recipient-permission-denied-title'
            ),
            body: this.translate.instant(
              'video-call.recipient-permission-denied-text'
            ),
            classname: 'bg-warning text-white',
            delay: 6000,
          });
        } else {
          this.notifyRejection();
        }
        this.blurActiveElement();
        try {
          if (this.activeModal) this.activeModal.close('rejected');
          else this.overlay.end();
        } catch {}
      }
    });
    this.subs.push(rj);

    const answeredElsewhereSub = this.vc
      .onCallAnsweredElsewhere()
      .subscribe((ev) => {
        if (!ev) return;
        if (
          !this.currentCallId ||
          !ev.callId ||
          ev.callId === this.currentCallId
        ) {
          this.stopRingtone();
          this.notifyAnsweredElsewhere();
          this.inPrompt = false;
          this.inCall = false;
          this.stopDurationTimer();
          this.blurActiveElement();
          try {
            if (this.activeModal) this.activeModal.close('answered-elsewhere');
            else this.overlay.end();
          } catch {}
        }
      });
    this.subs.push(answeredElsewhereSub);

    document.addEventListener('keydown', this.onKeyDown);

    const s2 = this.vc.getRemoteStream$().subscribe((s) => {
      this.currentRemoteStream = s;
      if (this.remoteVideo?.nativeElement) {
        if (!this.isRemoteVideoOff) {
          this.remoteVideo.nativeElement.srcObject = s;
          if (s && this.remoteVideo.nativeElement.paused)
            this.remoteVideo.nativeElement.play().catch(() => {});
        }
      }
    });
    this.subs.push(s2);

    const statusSub = this.vc
      .onVideoStatusChanged()
      .subscribe((evt: VideoStatusEvent) => {
        if (!evt?.callId || evt.callId !== this.currentCallId) return;
        const myId = this.currentUserService.GetSession()?.id;
        if (myId && evt.fromUserId === myId) return;
        this.isRemoteVideoOff = evt.isVideoOff;
        this.remoteVideoStatusFromEvent = evt.isVideoOff;
        this.updateRemoteVideoVisibility();
      });
    this.subs.push(statusSub);

    const audioStatusSub = this.vc
      .onAudioStatusChanged()
      .subscribe((evt: any) => {
        const myId = this.currentUserService.GetSession()?.id;
        if (!evt?.callId || evt.callId !== this.currentCallId) return;
        if (myId && evt.fromUserId === myId) return;
        this.isRemoteAudioOff = !!evt.isAudioOff;
        this.remoteAudioStatusFromEvent = !!evt.isAudioOff;
      });
    this.subs.push(audioStatusSub);

    const callStateSub = this.vc.getCallState().subscribe((state) => {
      if (this.inPrompt) return;
      if (state?.inCall) return;
      if (!this.inCall && !this.currentCallId) return;

      this.stopRingtone();
      this.inCall = false;
      this.stopDurationTimer();
      try {
        if (this.activeModal) this.activeModal.close('ended');
        else this.overlay.end();
      } catch {}
    });
    this.subs.push(callStateSub);
  }

  private updateRemoteVideoVisibility() {
    if (!this.remoteVideo?.nativeElement) return;

    if (this.isRemoteVideoOff) {
      this.remoteVideo.nativeElement.srcObject = null;
    } else {
      if (this.currentRemoteStream) {
        this.remoteVideo.nativeElement.srcObject = this.currentRemoteStream;
        this.remoteVideo.nativeElement.play().catch(() => {});
      }
    }
  }

  private blurActiveElement() {
    try {
      (document.activeElement as HTMLElement | null)?.blur();
    } catch {}
  }

  async accept() {
    if (!this.incomingCall) return;

    try {
      const outcome =
        await this.callMediaConsentOrchestrator.ensureConsentBeforeCall(
          this.incomingCall.callType ?? this.callType,
          { forceShow: true, action: 'join' }
        );
      if (outcome !== 'proceed') {
        try {
          await this.vc.rejectIncomingCall(
            this.incomingCall.callId,
            'PermissionDenied'
          );
        } catch {}
        this.stopRingtone();
        this.activeModal?.dismiss('permission-denied');
        return;
      }

      this.inPrompt = false;
      const video = this.isVideoCallType(
        this.incomingCall.callType ?? this.callType
      );
      const sendReceive = await this.resolveSendReceiveOptions(video);
      this.showPartialPermissionToast(sendReceive);
      await this.vc.acceptIncomingCall(this.incomingCall, {
        video: sendReceive.sendVideo,
        audio: sendReceive.sendAudio,
        receiveVideo: sendReceive.receiveVideo,
        receiveAudio: sendReceive.receiveAudio,
      });

      this.currentCallId = this.incomingCall.callId;
      this.bindStreams();
      this.inCall = true;
      this.startDurationTimer();

      setTimeout(() => this.stopRingtone(), 150);
    } catch (e) {
      if (this.isMediaPermissionError(e)) {
        this.mediaPermissionDenied = true;
        this.stopRingtone();
        await this.ensureCallEndedAfterPermissionDenied();
        try {
          this.cdr.detectChanges();
        } catch {}
        return;
      }
      console.error(e);
      this.stopRingtone();

      if (this.incomingCall && this.shouldRejectOnAcceptFailure(e)) {
        try {
          await this.vc.rejectIncomingCall(
            this.incomingCall.callId,
            'AcceptFailed'
          );
        } catch (rejectError) {
          console.error(
            'Failed to reject call after accept failure:',
            rejectError
          );
        }
      }

      this.activeModal?.dismiss('accept-failed');
    }
  }

  async reject() {
    this.stopRingtone();
    if (!this.incomingCall) return this.activeModal?.dismiss('rejected');
    try {
      await this.vc.rejectIncomingCall(this.incomingCall.callId);
      try {
        if (this.activeModal) this.activeModal.close('rejected');
        else this.overlay.end();
      } catch {}
    } catch {
      if (this.activeModal) this.activeModal.dismiss('reject-failed');
      else this.overlay.end();
    }
  }

  async hangup() {
    this.playEndToneOnce();
    this.sendRemoteHealthData();
    await this.vc.endCall();
    this.inCall = false;
    this.stopDurationTimer();
    this.blurActiveElement();
    try {
      if (this.activeModal) this.activeModal.close('ended');
      else this.overlay.end();
    } catch {}
  }

  async toggleMute() {
    if (!this.canSendAudio) {
      return;
    }
    this.isMuted = !this.isMuted;
    this.vc.muteAudio(this.isMuted);
    this.notifyRemoteMediaState();
  }

  async toggleVideo() {
    if (!this.canSendVideo) {
      return;
    }
    this.isVideoOff = !this.isVideoOff;
    this.vc.disableVideo(this.isVideoOff);
    this.notifyRemoteMediaState();
  }

  toggleMinimize() {
    if (!this.inCall) return;
    this.isMinimized = !this.isMinimized;

    try {
      if (this.isMinimized) this.overlay.minimize();
      else this.overlay.maximize();
    } catch {}

    try {
      if (this.activeModal) {
        if (this.isMinimized) {
          document.body.classList.add('vc-modal-minimized');
        } else {
          document.body.classList.remove('vc-modal-minimized');
        }
      }
    } catch {}

    try {
      if (this.isMinimized) {
        if (this.lastRemoteStream && this.minRemoteVideo?.nativeElement) {
          const el = this.minRemoteVideo.nativeElement;
          el.muted = false;
          el.srcObject = this.lastRemoteStream;

          if (this._minRemoteMetadataHandler) {
            el.removeEventListener(
              'loadedmetadata',
              this._minRemoteMetadataHandler
            );
            this._minRemoteMetadataHandler = undefined;
          }
          this._minRemoteMetadataHandler = () => {
            this.adjustRemoteFit(el, true);
          };
          el.addEventListener('loadedmetadata', this._minRemoteMetadataHandler);

          el.play()
            .then(() => {
              this.minPlaybackBlocked = false;
              try {
                this.cdr.detectChanges();
              } catch {}
            })
            .catch(() => {
              this.minPlaybackBlocked = true;
              try {
                this.cdr.detectChanges();
              } catch {}
            });
        }
      } else {
        try {
          this.cdr.detectChanges();
        } catch {}
        Promise.resolve().then(() => {
          try {
            if (this.lastRemoteStream && this.remoteVideo?.nativeElement) {
              const el = this.remoteVideo.nativeElement;
              el.muted = false;
              el.srcObject = this.lastRemoteStream;

              if (this._remoteMetadataHandler) {
                el.removeEventListener(
                  'loadedmetadata',
                  this._remoteMetadataHandler
                );
                this._remoteMetadataHandler = undefined;
              }
              this._remoteMetadataHandler = () => {
                this.adjustRemoteFit(el, false);
              };
              el.addEventListener(
                'loadedmetadata',
                this._remoteMetadataHandler
              );

              el.play().catch(() => {});
            }
            if (this.lastLocalStream && this.localVideo?.nativeElement) {
              try {
                const lel = this.localVideo.nativeElement;
                lel.muted = true;
                lel.srcObject = this.lastLocalStream;
                lel.play().catch(() => {});
              } catch {}
            }
          } catch {}
        });
      }
    } catch {}
    try {
      this.cdr.detectChanges();
    } catch {}
  }

  async resumeMinPlayback() {
    try {
      await this.audioUnlock.waitUntilUnlocked(2000).catch(() => {});
    } catch {}

    try {
      if (this.minRemoteVideo?.nativeElement && this.lastRemoteStream) {
        const el = this.minRemoteVideo.nativeElement;
        el.muted = false;
        el.srcObject = this.lastRemoteStream;
        await el.play();
        this.minPlaybackBlocked = false;
        try {
          this.cdr.detectChanges();
        } catch {}
      }
    } catch {
      this.minPlaybackBlocked = true;
      try {
        this.cdr.detectChanges();
      } catch {}
    }
  }

  private async startOutgoing() {
    try {
      const video = this.isVideoCallType(this.callType);
      await this.mediaPermissionStore.syncFromServer();
      const sendReceive = await this.resolveSendReceiveOptions(video);
      this.showPartialPermissionToast(sendReceive);

      const response = await this.vc.beginOutgoingCall({
        calleeUserId: this.calleeUserId,
        patientId: this.patientId,
        callType: this.callType,
        video: sendReceive.sendVideo,
        audio: sendReceive.sendAudio,
        receiveVideo: sendReceive.receiveVideo,
        receiveAudio: sendReceive.receiveAudio,
        callContextType: this.callContextType,
      });

      const { callId } = response;
      this.counterpartyUserId =
        this.normalizeOptionalString(response.counterpartyUserId) ||
        this.counterpartyUserId;
      this.counterpartyUserName =
        this.normalizeOptionalString(response.counterpartyUserName) ||
        this.counterpartyUserName;
      this.counterpartyProfessionalTitle =
        response.counterpartyProfessionalTitle ??
        this.counterpartyProfessionalTitle ??
        null;
      this.counterpartyBranchName =
        this.normalizeOptionalString(response.counterpartyBranchName) ||
        this.counterpartyBranchName;
      this.currentCallId = callId;
      this.bindStreams();
      this.inCall = true;
      this.startDurationTimer();
    } catch (e) {
      if (this.isMediaPermissionError(e)) {
        this.mediaPermissionDenied = true;
        await this.ensureCallEndedAfterPermissionDenied();
        try {
          this.cdr.detectChanges();
        } catch {}
        return;
      }
      console.error(e);
      this.activeModal?.dismiss('start-failed');
    }
  }

  private isMediaPermissionError(error: any): boolean {
    const name = (error?.name || '').toString();
    if (name === 'NotAllowedError' || name === 'NotFoundError') return true;
    const message = (error?.message || '').toString().toLowerCase();
    return (
      message.includes('permission') ||
      message.includes('notallowed') ||
      message.includes('denied')
    );
  }

  private async resolveSendReceiveOptions(isVideoCall: boolean): Promise<{
    sendAudio: boolean;
    sendVideo: boolean;
    receiveAudio: boolean;
    receiveVideo: boolean;
  }> {
    const callerPerms = this.mediaPermissionStore.permissions();
    const sendAudio = !!callerPerms.audioAllowed;
    const sendVideo = !!callerPerms.videoAllowed && isVideoCall;
    this.canSendAudio = sendAudio;
    this.canSendVideo = sendVideo;

    return {
      sendAudio,
      sendVideo,
      receiveAudio: true,
      receiveVideo: isVideoCall,
    };
  }

  private showPartialPermissionToast(opts: {
    sendAudio: boolean;
    sendVideo: boolean;
    receiveAudio: boolean;
    receiveVideo: boolean;
  }): void {
    const missingAudio = !opts.sendAudio;
    const missingVideo = opts.receiveVideo && !opts.sendVideo;
    if (!missingAudio && !missingVideo) return;

    let key = 'call.permission.partialAudio';
    if (missingAudio && missingVideo) {
      key = 'call.permission.partialAudioVideo';
    } else if (missingVideo) {
      key = 'call.permission.partialVideo';
    }

    this.toastService.show({
      header: this.translate.instant('general.warning'),
      body: this.translate.instant(key),
      classname: 'bg-warning text-white',
      delay: 5000,
    });
  }

  private showBrowserPermissionWarning(): void {
    if (this.browserPermissionToastShown) return;
    this.browserPermissionToastShown = true;
    this.toastService.show({
      header: this.translate.instant('video-call.permission-warning-title'),
      body: this.translate.instant('video-call.permission-warning-text'),
      classname: 'bg-warning text-white',
      delay: 6000,
    });
  }

  async exitAfterPermissionDenied(): Promise<void> {
    this.localPermissionDenied = true;
    try {
      if (this.incomingCall) {
        await this.reject();
        return;
      }
      if (this.inCall) {
        await this.hangup();
        return;
      }
      const state = this.vc.getCallStateValue?.();
      if (state?.inCall || state?.callId || this.currentCallId) {
        await this.vc.endCall('PermissionDenied');
        return;
      }
      if (this.activeModal) this.activeModal.dismiss('permission-denied');
      else this.overlay.end();
    } catch {}
  }

  refreshPage(): void {
    this.localPermissionDenied = true;
    try {
      if (this.incomingCall?.callId) {
        this.vc
          .rejectIncomingCall(this.incomingCall.callId, 'PermissionDenied')
          .catch(() => {});
      } else if (this.currentCallId || this.inCall) {
        this.vc.endCall().catch(() => {});
      }
      window.location.reload();
    } catch {}
  }

  private bindStreams() {
    const s1 = this.vc.getLocalStream$().subscribe((s) => {
      if (s) {
        console.debug('[VideoCall][Debug] outgoing_modal_bound_local_stream', {
          audioTracks: s.getAudioTracks().length,
          videoTracks: s.getVideoTracks().length,
        });
      }
      this.lastLocalStream = s || null;
      if (!this.initialLocalMediaSet) {
        this.initialLocalMediaSet = true;
        const audioTracks = s?.getAudioTracks() || [];
        const videoTracks = s?.getVideoTracks() || [];
        const hasAudioTrack = audioTracks.length > 0;
        const hasVideoTrack = videoTracks.length > 0;
        this.isMuted = !hasAudioTrack || audioTracks.every((t) => !t.enabled);
        this.isVideoOff = !hasVideoTrack || videoTracks.every((t) => !t.enabled);
        if (this.canSendAudio && !hasAudioTrack) {
          this.canSendAudio = false;
          this.showBrowserPermissionWarning();
        }
        if (this.canSendVideo && !hasVideoTrack) {
          this.canSendVideo = false;
          this.showBrowserPermissionWarning();
        }
        this.notifyRemoteMediaState();
      }

      if (this.localVideo?.nativeElement) {
        try {
          const el = this.localVideo.nativeElement;
          el.muted = true;
          el.srcObject = s;
          if (s && el.paused) el.play().catch(() => {});
        } catch {}
      }
    });
    const s2 = this.vc.getRemoteStream$().subscribe((s) => {
      if (s) {
        console.debug('[VideoCall][Debug] outgoing_modal_bound_remote_stream', {
          audioTracks: s.getAudioTracks().length,
          videoTracks: s.getVideoTracks().length,
        });
      }
      this.lastRemoteStream = s || null;
      const remoteVideoTracks = s?.getVideoTracks() || [];
      const remoteAudioTracks = s?.getAudioTracks() || [];
      const hasRemoteVideo = remoteVideoTracks.length > 0;
      const hasRemoteAudio = remoteAudioTracks.length > 0;
      const remoteVideoMuted =
        hasRemoteVideo &&
        remoteVideoTracks.every((t) => t.muted || !t.enabled);
      const remoteAudioMuted =
        hasRemoteAudio &&
        remoteAudioTracks.every((t) => t.muted || !t.enabled);
      const fallbackVideoOff = !hasRemoteVideo || remoteVideoMuted;
      const fallbackAudioOff = !hasRemoteAudio || remoteAudioMuted;
      this.isRemoteVideoOff =
        this.remoteVideoStatusFromEvent ?? fallbackVideoOff;
      this.isRemoteAudioOff =
        this.remoteAudioStatusFromEvent ?? fallbackAudioOff;

      remoteVideoTracks.forEach((t) => {
        t.onmute = () => {
          if (this.remoteVideoStatusFromEvent === undefined) {
            this.isRemoteVideoOff = true;
          }
          try {
            this.cdr.detectChanges();
          } catch {}
        };
        t.onunmute = () => {
          if (this.remoteVideoStatusFromEvent === undefined) {
            this.isRemoteVideoOff = false;
          }
          try {
            this.cdr.detectChanges();
          } catch {}
        };
        t.onended = () => {
          if (this.remoteVideoStatusFromEvent === undefined) {
            this.isRemoteVideoOff = true;
          }
          try {
            this.cdr.detectChanges();
          } catch {}
        };
      });

      remoteAudioTracks.forEach((t) => {
        t.onmute = () => {
          if (this.remoteAudioStatusFromEvent === undefined) {
            this.isRemoteAudioOff = true;
          }
          try {
            this.cdr.detectChanges();
          } catch {}
        };
        t.onunmute = () => {
          if (this.remoteAudioStatusFromEvent === undefined) {
            this.isRemoteAudioOff = false;
          }
          try {
            this.cdr.detectChanges();
          } catch {}
        };
        t.onended = () => {
          if (this.remoteAudioStatusFromEvent === undefined) {
            this.isRemoteAudioOff = true;
          }
          try {
            this.cdr.detectChanges();
          } catch {}
        };
      });

      if (this.remoteVideo?.nativeElement) {
        try {
          const el = this.remoteVideo.nativeElement;
          el.muted = false;
          el.srcObject = s;

          if (this._remoteMetadataHandler) {
            el.removeEventListener(
              'loadedmetadata',
              this._remoteMetadataHandler
            );
            this._remoteMetadataHandler = undefined;
          }
          this._remoteMetadataHandler = () => {
            this.adjustRemoteFit(el, false);
          };
          el.addEventListener('loadedmetadata', this._remoteMetadataHandler);

          if (s && el.paused) el.play().catch(() => {});
        } catch {}
      }

      if (this.minRemoteVideo?.nativeElement) {
        try {
          const el = this.minRemoteVideo.nativeElement;
          el.muted = false;
          el.srcObject = s;

          if (this._minRemoteMetadataHandler) {
            el.removeEventListener(
              'loadedmetadata',
              this._minRemoteMetadataHandler
            );
            this._minRemoteMetadataHandler = undefined;
          }
          this._minRemoteMetadataHandler = () => {
            this.adjustRemoteFit(el, true);
          };
          el.addEventListener('loadedmetadata', this._minRemoteMetadataHandler);

          if (s) {
            el.play()
              .then(() => {
                this.minPlaybackBlocked = false;
                try {
                  this.cdr.detectChanges();
                } catch {}
              })
              .catch(() => {
                this.minPlaybackBlocked = true;
                try {
                  this.cdr.detectChanges();
                } catch {}
              });
          }
        } catch {
          this.minPlaybackBlocked = true;
          try {
            this.cdr.detectChanges();
          } catch {}
        }
      }
    });
    this.subs.push(s1, s2);
  }

  private async ensureCallEndedAfterPermissionDenied(): Promise<void> {
    this.localPermissionDenied = true;
    if (!this.permissionDeniedToastShown) {
      this.permissionDeniedToastShown = true;
      this.toastService.show({
        header: this.translate.instant('video-call.permission-warning-title'),
        body: this.translate.instant('video-call.permission-warning-text'),
        classname: 'bg-warning text-white',
        delay: 6000,
      });
    }

    try {
      if (this.incomingCall?.callId && this.inPrompt) {
        await this.vc.rejectIncomingCall(
          this.incomingCall.callId,
          'PermissionDenied'
        );
        return;
      }
    } catch {}

    try {
      const state = this.vc.getCallStateValue?.();
      if (state?.inCall || state?.callId || this.currentCallId) {
        await this.vc.endCall('PermissionDenied');
      }
    } catch {}

    try {
      if (this.activeModal) this.activeModal.dismiss('permission-denied');
      else this.overlay.end();
    } catch {}
  }

  private adjustRemoteFit(el: HTMLVideoElement, isMinimized: boolean) {
    try {
      const vw = el.videoWidth || 0;
      const vh = el.videoHeight || 0;
      if (!vw || !vh) return;

      const container = el.closest('.videos') as HTMLElement | null;
      const contRect = container
        ? container.getBoundingClientRect()
        : el.parentElement
        ? el.parentElement.getBoundingClientRect()
        : null;
      if (!contRect) return;

      const containerW = Math.max(1, contRect.width);
      const containerH = Math.max(1, contRect.height);

      const scaleFill = Math.max(containerW / vw, containerH / vh);
      const UPSCALE_THRESHOLD = 1.1;

      if (isMinimized) {
        el.style.objectFit = 'contain';
        el.style.objectPosition = 'center';
        el.style.position = 'absolute';
        el.style.inset = '0';
        el.style.width = '100%';
        el.style.height = '100%';
      } else {
        if (scaleFill > UPSCALE_THRESHOLD) {
          el.style.objectFit = 'contain';
          el.style.objectPosition = 'center';
        } else {
          el.style.objectFit = 'cover';
          el.style.objectPosition = 'center';
        }
        el.style.position = 'absolute';
        el.style.inset = '0';
        el.style.width = '100%';
        el.style.height = '100%';
      }
    } catch {}
  }

  private notifyRemoteMediaState(): void {
    if (!this.currentCallId) return;
    this.vc.sendAudioStatus(this.currentCallId, this.isMuted).catch(() => {});
    if (this.isVideoCall) {
      this.vc
        .sendVideoStatus(this.currentCallId, this.isVideoOff)
        .catch(() => {});
    }
  }

  ngOnDestroy(): void {
    this.stopRingtone();
    this.subs.forEach((s) => s.unsubscribe());
    this.stopDurationTimer();
    this.removeDragListeners();

    document.removeEventListener('keydown', this.onKeyDown);

    try {
      document.body.classList.remove('vc-modal-minimized');
    } catch {}

    try {
      if (this.overlay.getActiveValue && this.overlay.getActiveValue()) {
        this.overlay.end();
      }
    } catch {}

    try {
      if (this.remoteVideo?.nativeElement && this._remoteMetadataHandler) {
        this.remoteVideo.nativeElement.removeEventListener(
          'loadedmetadata',
          this._remoteMetadataHandler
        );
        this._remoteMetadataHandler = undefined;
      }
    } catch {}
    try {
      if (
        this.minRemoteVideo?.nativeElement &&
        this._minRemoteMetadataHandler
      ) {
        this.minRemoteVideo.nativeElement.removeEventListener(
          'loadedmetadata',
          this._minRemoteMetadataHandler
        );
        this._minRemoteMetadataHandler = undefined;
      }
    } catch {}
  }

  private stopRingtone(): void {
    try {
      this.ringtoneAudio?.pause();
      if (this.ringtoneAudio) this.ringtoneAudio.currentTime = 0;
      this.ringtoneAudio = undefined;
    } catch {}
  }

  private playEndToneOnce(): void {
    console.log('playEndToneOnce called');
    if (this.endTonePlayed) return;
    this.endTonePlayed = true;
    this.playEndTone().catch(() => {});
  }

  private async playEndTone(): Promise<void> {
    await this.audioUnlock.waitUntilUnlocked(2000).catch(() => {});

    try {
      const audio = new Audio('/assets/sounds/call-ended.mp3');
      audio.volume = 0.6;
      await audio.play();
      return;
    } catch {}

    try {
      const AudioCtx =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = 600;
      o.connect(g);
      g.connect(ctx.destination);

      const now = ctx.currentTime;
      g.gain.setValueAtTime(0.0001, now);
      g.gain.exponentialRampToValueAtTime(0.15, now + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);

      o.start(now);
      o.stop(now + 0.3);

      setTimeout(() => {
        try {
          ctx.close();
        } catch {}
      }, 400);
    } catch {}
  }

  private notifyRejection(): void {
    try {
      this.toastService.show({
        header: this.translate.instant('video-call.call-ended'),
        body: this.translate.instant(
          'video-call.call-was-not-answered-or-rejected'
        ),
        classname: 'bg-danger text-white',
        delay: 3000,
      });
    } catch {}
  }

  private notifyAnsweredElsewhere(): void {
    try {
      this.toastService.show({
        header: this.translate.instant('video-call.call-ended'),
        body: this.translate.instant('video-call.answered-elsewhere'),
        classname: 'bg-warning text-dark',
        delay: 3000,
      });
    } catch {}
  }

  private shouldRejectOnAcceptFailure(error: any): boolean {
    const name = String(error?.name ?? '').toLowerCase();
    const message = String(error?.message ?? '').toLowerCase();
    const raw = `${name} ${message}`;

    if (
      raw.includes('callalreadyfinalized') ||
      raw.includes('answered-elsewhere') ||
      raw.includes('callansweredelsewhere') ||
      raw.includes('not authorized on this connection') ||
      raw.includes('not authorized to accept')
    ) {
      return false;
    }

    return true;
  }

  private startDurationTimer(): void {
    this.callStartTime = Date.now();
    this.callStartTimeDate = new Date();
    this.localPermissionDenied = false;
    this.remoteHealthSent = false;
    this.updateDuration();
    this.durationTimer = setInterval(() => this.updateDuration(), 1000);
  }

  private stopDurationTimer(): void {
    if (this.durationTimer) {
      clearInterval(this.durationTimer);
      this.durationTimer = undefined;
    }
    this.callStartTime = null;
    this.callStartTimeDate = null;
  }

  private updateDuration(): void {
    if (!this.callStartTime) {
      this.duration = '00:00';
      return;
    }
    const sec = Math.floor((Date.now() - this.callStartTime) / 1000);
    const hh = Math.floor(sec / 3600);
    const mm = Math.floor((sec % 3600) / 60);
    const ss = sec % 60;
    this.duration =
      (hh > 0 ? String(hh).padStart(2, '0') + ':' : '') +
      String(mm).padStart(2, '0') +
      ':' +
      String(ss).padStart(2, '0');
  }

  onMinHover(hover: boolean) {
    this.isMinHovered = hover;
  }

  onMinMouseDown(evt: MouseEvent) {
    try {
      const target = evt.target as HTMLElement | null;
      if (
        target &&
        target.closest &&
        target.closest('button, a, [data-no-drag], .btn')
      ) {
        return;
      }
    } catch {}

    if (evt.button !== 0) return;
    const el = (evt.currentTarget as HTMLElement).closest(
      '.minimized'
    ) as HTMLElement;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    this.dragActive = true;
    this.dragMoved = false;
    this.dragOffsetX = evt.clientX - rect.left;
    this.dragOffsetY = evt.clientY - rect.top;
    document.addEventListener('mousemove', this.onDocMouseMove);
    document.addEventListener('mouseup', this.onDocMouseUp);
    evt.preventDefault();
  }

  onMinTouchStart(evt: TouchEvent) {
    try {
      const t = evt.touches && evt.touches[0];
      if (!t) return;
      const target = (evt.target as HTMLElement) || null;
      if (
        target &&
        target.closest &&
        target.closest('button, a, [data-no-drag], .btn')
      ) {
        return;
      }
      const el = (evt.currentTarget as HTMLElement).closest(
        '.minimized'
      ) as HTMLElement;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      this.dragActive = true;
      this.dragMoved = false;
      this.dragOffsetX = t.clientX - rect.left;
      this.dragOffsetY = t.clientY - rect.top;

      document.addEventListener('touchmove', this.onDocTouchMove as any, {
        passive: false,
      });
      document.addEventListener('touchend', this.onDocTouchEnd as any);
      evt.preventDefault();
    } catch {}
  }

  private onDocTouchMove = (evt: TouchEvent) => {
    if (!this.dragActive) return;
    try {
      const t = evt.touches && evt.touches[0];
      if (!t) return;

      const width = this.minimizedWidth;
      const height = this.minimizedHeight;
      const newLeft = Math.max(
        8,
        Math.min(window.innerWidth - width - 8, t.clientX - this.dragOffsetX)
      );
      const newTop = Math.max(
        8,
        Math.min(window.innerHeight - height - 8, t.clientY - this.dragOffsetY)
      );

      try {
        const dx = Math.abs(
          t.clientX - (this.dragOffsetX + (this.minimizedLeft || 0))
        );
        const dy = Math.abs(
          t.clientY -
            (window.innerHeight -
              this.minimizedBottom -
              height +
              (this.dragOffsetY || 0))
        );
        if (dx > this.DRAG_THRESHOLD || dy > this.DRAG_THRESHOLD)
          this.dragMoved = true;
      } catch {}
      this.minimizedLeft = Math.round(newLeft);
      this.minimizedBottom = Math.round(window.innerHeight - newTop - height);
      evt.preventDefault();
    } catch {}
  };

  private onDocTouchEnd = (evt: TouchEvent) => {
    if (!this.dragActive) return;
    try {
      if (!this.dragMoved) {
        this.toggleMinimize();
      }
    } catch {}
    this.dragActive = false;
    this.removeDragListeners();
  };

  private onDocMouseMove = (evt: MouseEvent) => {
    if (this.resizeActive) {
      this.handleResize(evt);
      return;
    }
    if (!this.dragActive) return;
    try {
      const dx = Math.abs(
        evt.clientX - (this.dragOffsetX + (this.minimizedLeft || 0))
      );
      const dy = Math.abs(
        evt.clientY -
          (window.innerHeight -
            this.minimizedBottom -
            this.minimizedHeight +
            (this.dragOffsetY || 0))
      );
      if (dx > this.DRAG_THRESHOLD || dy > this.DRAG_THRESHOLD) {
        this.dragMoved = true;
      }
    } catch {}
    if (!this.dragActive) return;
    const width = this.minimizedWidth;
    const height = this.minimizedHeight;
    const newLeft = Math.max(
      8,
      Math.min(window.innerWidth - width - 8, evt.clientX - this.dragOffsetX)
    );
    const newTop = Math.max(
      8,
      Math.min(window.innerHeight - height - 8, evt.clientY - this.dragOffsetY)
    );
    this.minimizedLeft = Math.round(newLeft);
    this.minimizedBottom = Math.round(window.innerHeight - newTop - height);
  };

  private onDocMouseUp = (evt: MouseEvent) => {
    if (this.resizeActive) {
      this.resizeActive = false;
      this.removeResizeListeners();
      return;
    }
    if (!this.dragActive) return;
    try {
      if (!this.dragMoved) {
        this.toggleMinimize();
      }
    } catch {}
    this.dragActive = false;
    this.removeDragListeners();
  };

  private removeDragListeners() {
    document.removeEventListener('mousemove', this.onDocMouseMove);
    document.removeEventListener('mouseup', this.onDocMouseUp);
    document.removeEventListener('touchmove', this.onDocTouchMove as any);
    document.removeEventListener('touchend', this.onDocTouchEnd as any);
  }

  get minimizedStyle() {
    return {
      left: `${this.minimizedLeft}px`,
      bottom: `${this.minimizedBottom}px`,
      width: `${this.minimizedWidth}px`,
      height: `${this.minimizedHeight}px`,
    };
  }

  onResizeMouseDown(evt: MouseEvent, edge: string) {
    evt.preventDefault();
    evt.stopPropagation();

    this.resizeActive = true;
    this.resizeEdge = edge;
    this.resizeStartX = evt.clientX;
    this.resizeStartY = evt.clientY;
    this.resizeStartWidth = this.minimizedWidth;
    this.resizeStartHeight = this.minimizedHeight;
    this.resizeStartLeft = this.minimizedLeft;
    this.resizeStartBottom = this.minimizedBottom;

    document.addEventListener('mousemove', this.onDocMouseMove);
    document.addEventListener('mouseup', this.onDocMouseUp);
  }

  private handleResize(evt: MouseEvent) {
    const MIN_WIDTH = 120;
    const MIN_HEIGHT = 90;
    const MAX_WIDTH = 600;
    const MAX_HEIGHT = 400;
    const PADDING = 8;

    const mouseX = evt.clientX;
    const mouseY = evt.clientY;

    let newWidth = this.minimizedWidth;
    let newHeight = this.minimizedHeight;
    let newLeft = this.minimizedLeft;
    let newBottom = this.minimizedBottom;

    // Calculate the current top position
    const currentTop =
      window.innerHeight - this.minimizedBottom - this.minimizedHeight;

    // Handle horizontal resize
    if (this.resizeEdge.includes('right')) {
      const requestedWidth = mouseX - this.minimizedLeft;
      newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, requestedWidth));
      newWidth = Math.min(
        newWidth,
        window.innerWidth - this.minimizedLeft - PADDING
      );
    } else if (this.resizeEdge.includes('left')) {
      const requestedLeft = mouseX;
      const maxLeft = this.minimizedLeft + this.minimizedWidth - MIN_WIDTH;
      newLeft = Math.max(PADDING, Math.min(maxLeft, requestedLeft));
      newWidth = this.minimizedLeft + this.minimizedWidth - newLeft;
      newWidth = Math.min(MAX_WIDTH, newWidth);
    }

    // Handle vertical resize
    if (this.resizeEdge.includes('top')) {
      const requestedTop = mouseY;
      const maxTop = window.innerHeight - this.minimizedBottom - MIN_HEIGHT;
      const constrainedTop = Math.max(PADDING, Math.min(maxTop, requestedTop));
      newHeight = window.innerHeight - this.minimizedBottom - constrainedTop;
      newHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, newHeight));
      newBottom = this.minimizedBottom;
    } else if (this.resizeEdge.includes('bottom')) {
      const requestedBottom = window.innerHeight - mouseY;
      const maxBottom = window.innerHeight - currentTop - MIN_HEIGHT;
      newBottom = Math.max(PADDING, Math.min(maxBottom, requestedBottom));
      newHeight = window.innerHeight - currentTop - newBottom;
      newHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, newHeight));
    }

    if (newLeft + newWidth > window.innerWidth - PADDING) {
      newWidth = window.innerWidth - newLeft - PADDING;
    }
    if (newBottom + newHeight > window.innerHeight - PADDING) {
      newHeight = window.innerHeight - newBottom - PADDING;
    }

    this.minimizedWidth = Math.round(newWidth);
    this.minimizedHeight = Math.round(newHeight);
    this.minimizedLeft = Math.round(newLeft);
    this.minimizedBottom = Math.round(newBottom);
  }

  private removeResizeListeners() {
    document.removeEventListener('mousemove', this.onDocMouseMove);
    document.removeEventListener('mouseup', this.onDocMouseUp);
  }

  private async sendRemoteHealthData(): Promise<void> {
    if (this.remoteHealthSent) return;
    this.remoteHealthSent = true;
    const user = this.currentUserService.GetSession();
    if (!user?.isUsbsIntegrated) {
      console.warn(
        'Remote health data not sent: user is not integrated with USBS'
      );
      return;
    }

    if (!this.callStartTimeDate || !this.patientId) {
      console.warn(
        'Remote health data not sent: missing start time or patient'
      );
      return;
    }

    const command: SendRemoteHealthModel = {
      patientId: this.patientId,
      sessionStartTime: this.callStartTimeDate,
      sessionEndTime: new Date(),
      hasVideoRecording: false,
      hasAudioRecording: false,
      hasFileSent: false,
    };

    const result = await this.remoteHealthService.sendRemoteHealth(command);
    if (result && result.isSuccess) {
      console.log('Remote health data sent successfully to e-Nabız 653');
    } else {
      console.error('Failed to send remote health data to e-Nabız', result);
    }
  }

  private normalizeOptionalString(value: unknown): string {
    if (value == null) return '';
    return value.toString().trim();
  }
}
